import PageHeader from "components/PageHeader/PageHeader";
import { FC, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { getPdfs } from "data/api";
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

interface DownloadItem {
  id: number;
  name: string;
  href: string;
}

const CARD_GRADIENTS = [
  "from-rose-600 via-red-600 to-orange-500",
  "from-blue-700 via-indigo-600 to-violet-600",
  "from-emerald-600 via-teal-600 to-cyan-500",
  "from-amber-500 via-orange-500 to-red-500",
  "from-slate-700 via-blue-800 to-indigo-900",
  "from-fuchsia-600 via-purple-600 to-indigo-600",
];

const formatPdfName = (url: string) => {
  const filename = decodeURIComponent(url.split("/").pop() || "document");
  return filename
    .replace(/\.pdf$/i, "")
    .replace(/[_+-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
};

const DownloadCard: FC<{ item: DownloadItem; index: number }> = ({
  item,
  index,
}) => {
  const gradient = CARD_GRADIENTS[index % CARD_GRADIENTS.length];
  const title = item.name || formatPdfName(item.href);

  return (
    <article className="group relative flex flex-col rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 border border-neutral-200/80 dark:border-neutral-700/80 shadow-bcr-card hover:shadow-bcr-card-hover transition-all duration-300 hover:-translate-y-1">
      <div
        className={`relative h-28 sm:h-32 bg-gradient-to-br ${gradient} shrink-0`}
      >
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_80%_20%,white,transparent_55%)]" />
        <div className="absolute bottom-3 left-4 right-4">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-white/20 text-white backdrop-blur-sm border border-white/25">
            <DocumentTextIcon className="w-3 h-3" />
            PDF document
          </span>
        </div>
      </div>

      <div className="relative flex justify-center -mt-14 px-5">
        <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-2xl bg-white dark:bg-neutral-800 border-4 border-white dark:border-neutral-900 shadow-xl flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
          <DocumentTextIcon className="w-12 h-12 sm:w-14 sm:h-14 text-red-600 dark:text-red-400" />
        </div>
      </div>

      <div className="flex flex-col flex-1 px-5 sm:px-6 pt-4 pb-6 text-center">
        <h3
          className="text-base sm:text-lg font-bold text-neutral-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors line-clamp-2"
          title={title}
        >
          {title}
        </h3>
        <p className="mt-3 text-xs sm:text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">
          Product catalogue & technical documentation
        </p>
        <div className="mt-5 pt-4 border-t border-neutral-100 dark:border-neutral-800">
          <a
            href={item.href}
            target="_blank"
            rel="noopener noreferrer"
            download
            className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 rounded-xl text-sm font-semibold bg-[#0f1b3d] hover:bg-[#0a1628] text-white transition-colors"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            Download PDF
          </a>
        </div>
      </div>
    </article>
  );
};

const DownloadsPage: FC = () => {
  const [downloads, setDownloads] = useState<DownloadItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getPdfs()
      .then((res) => {
        const attachments = res?.pdf_attachments;
        if (Array.isArray(attachments)) {
          setDownloads(
            attachments.map((url: string, index: number) => ({
              id: index + 1,
              name: formatPdfName(url),
              href: url,
            }))
          );
        }
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div
      className="nc-DownloadsPage relative overflow-hidden"
      data-nc-id="DownloadsPage"
    >
      <Helmet>
        <title>Downloads | BCR LTD. UK</title>
        <meta
          name="description"
          content="Download BCR Ltd product catalogues, technical specifications, and brochures for expansion joint covers, tile trims, LED profiles, and building chemicals."
        />
      </Helmet>

      <PageHeader
        title="Downloads"
        subtitle="Download our latest catalogues, technical specifications, and product brochures."
      />

      <div className="container relative px-4 sm:px-6 py-10 sm:py-12 pb-16 sm:pb-24">
        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-2xl border border-neutral-200 dark:border-neutral-700 overflow-hidden"
              >
                <div className="h-28 bg-neutral-200 dark:bg-neutral-700" />
                <div className="p-6 flex flex-col items-center">
                  <div className="w-24 h-24 -mt-14 rounded-2xl bg-neutral-300 dark:bg-neutral-600" />
                  <div className="mt-6 h-4 w-2/3 rounded bg-neutral-200 dark:bg-neutral-700" />
                  <div className="mt-4 h-10 w-full rounded-xl bg-neutral-200 dark:bg-neutral-700" />
                </div>
              </div>
            ))}
          </div>
        ) : downloads.length === 0 ? (
          <div className="text-center py-16 px-6 rounded-2xl border border-dashed border-neutral-300 dark:border-neutral-600 bg-neutral-50 dark:bg-neutral-800/40">
            <DocumentTextIcon className="w-12 h-12 mx-auto text-neutral-400" />
            <h3 className="mt-4 text-lg font-semibold text-neutral-900 dark:text-white">
              No documents available
            </h3>
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">
              Check back soon or contact us for specific technical documents.
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {downloads.map((item, index) => (
              <DownloadCard key={`${item.href}-${item.id}`} item={item} index={index} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DownloadsPage;
